import { useState, useEffect, useRef, useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { FlashDeal, FlashDealProduct } from "../../hooks/useFlashDeals";
import { getSupabaseProductImageUrl } from "../../utils/imageUtils";

interface FlashDealsProps {
  deals: FlashDeal[];
  onAddToCart?: (product: FlashDealProduct) => void;
  onQuickView?: (product: FlashDealProduct) => void;
}

const D = {
  gold: "#c9a96e",
  goldLight: "#e8d5b0",
  black: "#111111",
  white: "#ffffff",
  muted: "#737373",
  border: "#e0e0e0",
  bg: "#f0eee9",
  serif: "'Cormorant Garamond', Georgia, serif",
  sans: "'DM Sans', system-ui, sans-serif",
} as const;

function CountUnit({ value, label, small }: { value: number; label: string; small?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: small ? 2 : 3 }}>
      <span style={{
        fontFamily: D.sans,
        fontSize: small ? 15 : 20,
        fontWeight: 600,
        color: D.black,
        letterSpacing: 1,
        lineHeight: 1,
        minWidth: small ? 30 : 40,
        textAlign: "center",
      }}>
        {String(value).padStart(2, "0")}
      </span>
      <span style={{ fontSize: small ? 8 : 9, color: D.muted, textTransform: "uppercase", letterSpacing: 1 }}>
        {label}
      </span>
    </div>
  );
}

function GoldColon({ small }: { small?: boolean }) {
  return (
    <span style={{
      fontFamily: D.sans,
      fontWeight: 300,
      fontSize: small ? 13 : 17,
      color: D.gold,
      marginBottom: small ? 10 : 12,
      lineHeight: 1,
    }}>:</span>
  );
}

function NavArrow({ dir, active, onClick }: { dir: "left" | "right"; active: boolean; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={!active}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 32,
        height: 32,
        border: `1px solid ${active && hov ? D.black : D.border}`,
        background: "transparent",
        color: active ? D.black : D.border,
        cursor: active ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        transition: "border-color 0.15s, color 0.15s",
        flexShrink: 0,
      }}
    >
      {dir === "left" ? <ChevronLeft size={13} /> : <ChevronRight size={13} />}
    </button>
  );
}

function ProductCard({
  product, sale, original, pct, imgSrc, onClick,
}: {
  product: FlashDealProduct;
  sale: number;
  original: number;
  pct: number;
  imgSrc: string;
  onClick: () => void;
}) {
  const [hov, setHov] = useState(false);
  const hasDiscount = pct > 0;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flexShrink: 0,
        width: "clamp(140px, 15vw, 180px)",
        background: D.white,
        border: `1px solid ${hov ? "#bbb" : D.border}`,
        cursor: "pointer",
        transition: "border-color 0.15s",
        scrollSnapAlign: "start",
      }}
    >
      <div style={{ position: "relative", height: "clamp(140px, 17vw, 200px)", overflow: "hidden" }}>
        <img
          src={imgSrc}
          alt={product.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s",
            transform: hov ? "scale(1.05)" : "scale(1)",
          }}
          loading="lazy"
        />
        {hasDiscount && (
          <div style={{
            position: "absolute",
            top: 10,
            left: 10,
            background: D.black,
            color: D.white,
            fontFamily: D.sans,
            fontSize: 10,
            fontWeight: 600,
            padding: "3px 7px",
            letterSpacing: 0.5,
          }}>−{pct}%</div>
        )}
      </div>

      <div style={{ padding: "10px 12px 12px", borderTop: `1px solid ${D.border}` }}>
        <div style={{
          fontFamily: D.sans,
          fontSize: 10,
          fontWeight: 500,
          color: D.black,
          letterSpacing: "0.8px",
          textTransform: "uppercase",
          marginBottom: 6,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>{product.name}</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{
            fontFamily: D.serif,
            fontSize: hasDiscount ? 18 : 16,
            fontWeight: 500,
            color: D.black,
          }}>${sale.toFixed(2)}</span>
          {hasDiscount && original > sale && (
            <span style={{ fontFamily: D.sans, fontSize: 11, color: D.muted, textDecoration: "line-through" }}>
              ${original.toFixed(2)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ViewAllCard({ onClick }: { onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flexShrink: 0,
        width: 120,
        background: D.white,
        border: `1px solid ${hov ? D.black : D.border}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        cursor: "pointer",
        padding: 20,
        scrollSnapAlign: "start",
        transition: "border-color 0.15s",
      }}
    >
      <span style={{
        fontFamily: D.sans,
        fontSize: 9,
        fontWeight: 600,
        color: D.black,
        textTransform: "uppercase",
        letterSpacing: "1.5px",
        textAlign: "center",
        lineHeight: 1.6,
      }}>View<br />All</span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={D.black} strokeWidth="1.5" strokeLinecap="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    </div>
  );
}

const FlashDeals: FC<FlashDealsProps> = ({ deals }) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const activeDeal = deals[0] as FlashDeal | undefined;

  const [secondsLeft, setSecondsLeft] = useState(activeDeal?.seconds_remaining ?? 0);

  useEffect(() => {
    setSecondsLeft(activeDeal?.seconds_remaining ?? 0);
  }, [activeDeal?.seconds_remaining]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(
      () => setSecondsLeft((p) => (p > 0 ? p - 1 : 0)),
      1000,
    );
    return () => clearInterval(id);
  }, [secondsLeft > 0]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -600 : 600, behavior: "smooth" });
  };

  if (!activeDeal || activeDeal.products.length === 0) return null;

  const allProducts = deals.flatMap((d) => d.products);
  const days = Math.floor(secondsLeft / 86400);
  const hrs = Math.floor((secondsLeft % 86400) / 3600);
  const min = Math.floor((secondsLeft % 3600) / 60);
  const sec = secondsLeft % 60;

  const imgUrl = (p: FlashDealProduct) =>
    getSupabaseProductImageUrl(p.image) ?? "https://via.placeholder.com/400x400";

  return (
    <section style={{ background: D.bg, fontFamily: D.sans }} className="border-b border-section-border">
      {/* Gold top rule */}
      <div style={{ height: 2, background: `linear-gradient(90deg, ${D.gold} 0%, ${D.goldLight} 50%, ${D.gold} 100%)` }} />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="px-4 md:px-10 pt-5 md:pt-8">
          <div className="flex items-start md:items-end justify-between gap-3 md:gap-6 mb-4 md:mb-6">
            {/* Left: label + title */}
            <div>
              <div style={{ fontSize: 9, fontWeight: 500, color: D.gold, textTransform: "uppercase", letterSpacing: "2.5px", marginBottom: 6 }}>
                Limited Time Offer
              </div>
              <div style={{
                fontFamily: D.serif,
                fontSize: "clamp(28px, 4vw, 42px)",
                fontWeight: 400,
                color: D.black,
                letterSpacing: "0.3px",
                lineHeight: 1,
                fontStyle: "italic",
              }}>
                {activeDeal.name || "Today's Deals"}
              </div>
            </div>

            {/* Right: countdown + divider + nav */}
            <div className="flex items-center gap-3 md:gap-6 pb-0 md:pb-1.5">
              {/* Desktop countdown */}
              <div className="hidden md:flex items-center gap-1.5">
                <span style={{ fontSize: 10, color: D.muted, letterSpacing: "0.5px", textTransform: "uppercase", marginRight: 4 }}>
                  Ends in
                </span>
                <CountUnit value={days} label="Days" />
                <GoldColon />
                <CountUnit value={hrs} label="Hrs" />
                <GoldColon />
                <CountUnit value={min} label="Min" />
                <GoldColon />
                <CountUnit value={sec} label="Sec" />
              </div>

              {/* Mobile compact countdown */}
              <div className="flex md:hidden items-center gap-1">
                <CountUnit value={days} label="D" small />
                <GoldColon small />
                <CountUnit value={hrs} label="H" small />
                <GoldColon small />
                <CountUnit value={min} label="M" small />
                <GoldColon small />
                <CountUnit value={sec} label="S" small />
              </div>

              {/* Separator + arrows — desktop only */}
              <div className="hidden md:block" style={{ width: 1, height: 28, background: D.border }} />
              <div className="hidden md:flex gap-1.5">
                <NavArrow dir="left" active={canScrollLeft} onClick={() => scroll("left")} />
                <NavArrow dir="right" active={canScrollRight} onClick={() => scroll("right")} />
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-4 md:mx-10" style={{ height: 1, background: D.border }} />

        {/* Product track */}
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          className="flex overflow-x-auto px-4 py-6 md:px-10 md:py-8"
          style={{
            gap: 1,
            background: D.border,
            scrollSnapType: "x mandatory",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          } as React.CSSProperties}
        >
          {allProducts.map((product) => {
            const pct = product.sale_rate > 0 ? Math.round(product.sale_rate) : 0;
            return (
              <ProductCard
                key={product.id}
                product={product}
                sale={product.display_sale_price}
                original={product.display_price}
                pct={pct}
                imgSrc={imgUrl(product)}
                onClick={() => navigate(`/product/${product.id}`)}
              />
            );
          })}
          <ViewAllCard onClick={() => navigate("/products")} />
        </div>

        {/* Bottom bar */}
        <div className="flex items-center justify-between px-4 md:px-10 pt-2.5 pb-5">
          <span style={{ fontSize: 11, color: D.muted }}>
            {allProducts.length} products
          </span>
          <button
            onClick={() => navigate("/products")}
            style={{
              fontFamily: D.sans,
              fontSize: 11,
              fontWeight: 500,
              color: D.black,
              background: "none",
              border: "none",
              cursor: "pointer",
              letterSpacing: "0.5px",
              textDecoration: "underline",
              textDecorationColor: D.gold,
              textUnderlineOffset: 3,
            }}
          >
            Shop all deals →
          </button>
        </div>
      </div>
    </section>
  );
};

export default FlashDeals;
